import {
    IEventInformationData,
    IField,
    IItinerarymap,
    IResponse,
    IValueData,
    OrgListElement,
} from '@eventr/common'
import { IExpression } from '../feat/editItinerary/editComponents/arrayFinder'
import { useGuestStore } from '../feat/guest/guest.store'
import { IItinerary } from '../state/interfaces'
import { IAppUpdate } from '../state/itineraryState/currentItin.interfaces'
import { RocketChannel } from '../state/rocketchat/rocketchat.interface'
import { requestShareCode, requestsItinerary } from './api'
import { gwApiClient, wpApiClient } from './apiClients'

export const DEBUG_RENDER_OPTIONS = true

export interface IUserWithTag {
    type: string
    option: number
}

export interface IValues {
    id: number
    section: number
    itinerary: number
    value: any
}

export interface ISection {
    id: number
    name: string
    time_created: string
    time_updated: string
    properties?: ISectionProperties
}

export enum ExcelDisplayTypes {
    table = 'table',
    linnear = 'linnear',
}
export interface IDataSourceProperties {
    source?: number
    options?: string[]
}
export enum DataSourceTypes {
    users = 'users',
    usersWithTags = 'usersWithTags',
    parent = 'parent',
    timezones = 'timezones',
    element = 'element',
    events = 'events',
    days = 'days',
    timeDifference = 'timeDifference',
    listOfValues = 'listOfValues',
}

export interface ISectionProperties {
    jsonName?: string
    hidden?: boolean
    sectionDisplayName?: string
    sectionDisplayNameTeams?: string
    icon?: string
    renderer?: string
    rendererOptions?: object
    version?: number
    mobileEditFieldOptions?: {
        components: IEditComponent[]
        headerText?: string
        icon?: string
        underButtonText?: string
        addButtonText?: string
        deleteButtonText?: string
        headerKeyValue?: number
        subPages?: ISubPage[]
    }
}
export interface ISubPage {
    type: string
    renderOptions?: any
    headerText?: string
    fieldId?: number
    addButtonText?: string
    components?: IEditComponent[]
    onAddPushTo?: number
    deleteButtonText?: string
    userListFieldId?: number
}
export interface IEditComponent {
    type: string
    fieldId?: number
    components?: IEditComponent[]
    settings?: IExpression | any
    headerText?: string
    headerIcon?: string
    iconColour?: string
    userListFieldId?: number
    multiBooking?: IMultiBookingParams
}

export interface IMultiBookingParams {
    params: string[]
    format: string
}

export interface IBodyUpdateSingleValue {
    old_value_with_UUID: string
    new_value: string
    section_id: number
    itin_id: number
    update_app: boolean
}
export interface IBodyAddUserToEntity {
    user_id: number
    object_id: string
    users_path: string
}

export interface IBodyCreateItinerary {
    name: string
}

export interface IGeneralInfo {
    itinerary_id: number
    section_id: number
    value: IEventInformationData
}

export interface FlightBooking {
    bookref: string
    flightDate: string
    actualBookingRef?: string
    outboundAirportAbr?: string
    inboundAirportAbr?: string
    flight_number?: string
    flight_id?: string
    departure?: {
        dep_estimated: string
        dep_gate?: string
        dep_name?: string
        dep_terminal?: string
        dep_time?: string
    }
    arrival?: {
        arr_estimated: string
        arr_gate?: string
        arr_name?: string
        arr_terminal?: string
        arr_time?: string
    }
    status?: string
    airline?: string
    passengers?: IUserWithTag[]
    lastUpdatedUtc?: string
    lastModifiedUnix?: number
    _id?: string
}

export interface ItineraryMapWithValue {
    id: number
    name: string
    properties: string
    time_created: number
    time_updated: number
    value: string
}
const WP_ITIN_BASE_URL = 'itinerary/v1/'

const ITINS_BASE_URL = `${WP_ITIN_BASE_URL}itineraries`
const ENDPOINTS = {
    team: {
        itineraries: {
            copyItinerary: `${ITINS_BASE_URL}/copyItinerary`,
            removePublic: `${ITINS_BASE_URL}/removePublic`,
            updateValues: `${ITINS_BASE_URL}/updateValues`,
        },
        getAPIUpdates: `${WP_ITIN_BASE_URL}apiUpdates/get`,
        todo: {
            upsertTodo: `${WP_ITIN_BASE_URL}todo`,
        },
        values: {
            getAll: (itinId: number) =>
                `${WP_ITIN_BASE_URL}valueData/v2/${itinId}`,
            upsertElement: (itinId: number, sectionId: number) =>
                `${WP_ITIN_BASE_URL}values/upsert/${itinId}/${sectionId}`,
            deleteElement: (itinId: number, sectionId: number) =>
                `${WP_ITIN_BASE_URL}values/delete/${itinId}/${sectionId}`,
        },
        rocketchat: {
            getChannels: (itin_id: number) =>
                `${WP_ITIN_BASE_URL}rocket/getChannels/${itin_id}`,
        },
    },
}

export const DataAPI = {
    getItineraryMap: async (
        orgID?: string,
        withHeaders?: boolean
    ): Promise<IItinerarymap[]> => {
        let resp = null
        if (useGuestStore.getState().isGuestMode && !orgID) {
            await useGuestStore
                .getState()
                .resolveGuestData(useGuestStore.getState().guestCode)
            resp = [useGuestStore.getState().guestData.map_entry]
        } else {
            resp = await requestsItinerary.get<IItinerarymap[]>(
                'itineraryMap',
                { ORG_OVERRIDE: orgID, WITH_HEADERS: withHeaders }
            )
        }
        // loop  over resp and change id and itinerary_version fro string to number
        if (resp.length)
            resp.forEach((r) => {
                r.id = Number(r.id)
                r.itinerary_version = Number(r.itinerary_version)
            })

        return resp
    },

    getItineraryMapWithInfo: async (): Promise<ItineraryMapWithValue[]> => {
        const resp = await requestsItinerary.get<ItineraryMapWithValue[]>(
            'itineraries/withDates'
        )
        return resp
    },

    getData: async (itineraryMapEntry: IItinerarymap): Promise<IItinerary> => {
        const path = itineraryMapEntry.itinerary_endpoint.split('v1/')[1]

        let resp: IItinerary = null
        if (useGuestStore.getState().isGuestMode) {
            resp = useGuestStore.getState().guestData.itinerary
        } else {
            resp = await requestsItinerary.get<IItinerary>(path)
        }
        if (DEBUG_RENDER_OPTIONS) {
            let debugResp = resp

            debugResp.v2.forEach((section) => {
                section.rendererOptions = null
            })
            debugResp.version = Number.parseInt(
                itineraryMapEntry.itinerary_version
            )
            return debugResp
        }
        resp.version = Number.parseInt(itineraryMapEntry.itinerary_version)
        return resp
    },

    getSections: async (): Promise<ISection[]> => {
        const resp = await requestsItinerary.get<any>('sections', {
            NOT_IN_GUEST: true,
        })
        resp.forEach((r: any) => {
            if (r.properties) r.properties = JSON.parse(r.properties as string) //someday I will change backend to send an object instead string for now it will do
        })
        return resp
    },

    getFields: async (section_id?: number): Promise<IField[]> => {
        const res = await requestsItinerary.get<IField[]>(
            'fields/' + (section_id ?? ''),
            { NOT_IN_GUEST: true }
        )

        res.forEach((r) => {
            r.id = Number.parseInt(r.id as any)
            r.section = Number.parseInt(r.section as any)
            r.position = Number.parseInt(r.position as any)
            r.parent = r.parent ? Number.parseInt(r.parent as any) : null
            if (r.type_properties)
                r.type_properties = JSON.parse(r.type_properties as string) //someday I will change backend to send an object instead string for now it will do
        })
        return res
    },

    updateSection: async (
        itinId: number,
        sectionId: string,
        body: {}
    ): Promise<any> => {
        const resp = await requestsItinerary.post(
            `values/updateSection/${itinId}/${sectionId}`,
            body
        )
        return resp
    },

    deleteBooking: async (
        ids: string,
        section: string,
        itinerary: number
    ): Promise<any> => {
        const resp = await requestsItinerary.delete(
            `values/delete-app/${section}/${itinerary}/${ids}`
        )
        return resp
    },

    /// IResponce is not the correct responce type here. The one used in back office might be better but both contain the expected data.
    createItinerary: async (
        body: IBodyCreateItinerary
    ): Promise<IResponse<{ itineraryID?: number }>> => {
        const resp = await requestsItinerary.post('itineraries/create', body)
        return resp
    },

    deleteItinerary: async (id: number): Promise<number> => {
        const resp = await requestsItinerary.delete(`itineraries/delete/${id}`)
        return resp
    },

    createUpdateGeneralInfo: async (body: IGeneralInfo): Promise<any> => {
        const resp = await requestsItinerary.post(
            'values/createUpdateGeneralInfo',
            body
        )
        return resp
    },

    getFlightData: async (
        flightNumber: string,
        date: string,
        body: OrgListElement
    ): Promise<any> => {
        const resp = await requestsItinerary.post(
            `flights/getFlights/${flightNumber}/${date}`,
            body
        )
        return resp
    },

    getCalendar: async (id: number): Promise<any> => {
        const resp = await requestsItinerary.get(`ical/${id}`, {
            NOT_IN_GUEST: true,
        })
        return resp
    },
    getShareCodeInfo: async (url: string): Promise<any> => {
        const resp = await requestShareCode.get(url)
        return resp
    },
    guest: {
        getGuestData: async (code: number): Promise<any> => {
            const resp = await gwApiClient.get(`guest?data=${code}`, {
                NO_AUTH: true,
            })
            return resp
        },
        duplicateItinerary: async (orgId: string, body: any) => {
            const resp = await wpApiClient.post<any, number>(
                ENDPOINTS.team.itineraries.copyItinerary,
                body,
                {
                    ORG_OVERRIDE: orgId,
                }
            )
            return resp
        },
    },
    getAPIUpdates: async (
        flights: any[],
        itinId: number
    ): Promise<IResponse<IAppUpdate>> => {
        const resp = await wpApiClient.post<any, IAppUpdate>(
            ENDPOINTS.team.getAPIUpdates,
            { flights, itinId },
            { NOT_IN_GUEST: true }
        )
        return resp
    },
    itineraries: {
        updateValue: async (itinId: number): Promise<IResponse<any>> => {
            return await wpApiClient.post(
                ENDPOINTS.team.itineraries.updateValues,
                {
                    itinId: itinId,
                    itin_id: itinId,
                }
            )
        },
        removePublicItin: async (
            itinId: number,
            code: string
        ): Promise<IResponse<any>> => {
            return await wpApiClient.post(
                ENDPOINTS.team.itineraries.removePublic,
                {
                    code,
                    itinID: itinId,
                }
            )
        },
    },
    todo: {
        upsertTodo: async (body: TodoUpsert): Promise<IResponse<any>> => {
            return await wpApiClient.post(ENDPOINTS.team.todo.upsertTodo, body)
        },
    },
    editValues: {
        getAll: async (itinId: number): Promise<IResponse<IValueData>> => {
            return await wpApiClient.get<IValueData>(
                ENDPOINTS.team.values.getAll(itinId)
            )
        },
        upsertElement: async (
            itinId: number,
            sectionId: number,
            element: any
        ): Promise<IResponse<any>> => {
            return await wpApiClient.post<any, any>(
                ENDPOINTS.team.values.upsertElement(itinId, sectionId) +
                    `?origin=app&publish=true`,
                element
            )
        },
        deleteElement: async (
            itinId: number,
            sectionId: number,
            elementId?: string
        ): Promise<IResponse<any>> => {
            return await wpApiClient.delete(
                ENDPOINTS.team.values.deleteElement(itinId, sectionId) +
                    `?origin=app&publish=true${
                        elementId ? '&object_id=' + elementId : ''
                    }`
            )
        },
    },
    rocketchat: {
        getChannels: async (
            itin_id: number
        ): Promise<IResponse<RocketChannel[]>> => {
            return await wpApiClient.get(
                ENDPOINTS.team.rocketchat.getChannels(itin_id),
                {
                    NOT_IN_GUEST: true,
                }
            )
        },
    },
}

interface TodoUpsert {
    filler: {
        status: 'completed' | 'incomplete'
    }
    object_id: string
    itinerary_id: number
    filler_id?: number
}
